import React, {Component, Fragment} from 'react';
import {compose, graphql} from 'react-apollo';
import {isNil, includes} from 'lodash';
import PropTypes from 'prop-types';

import Modal from '@homeaway/react-modal';
import edap from '@homeaway/edap-wrapper';
import Experiment, {Off, On} from '@homeaway/react-experiment';
import {ButtonPrimary, Button} from '@homeaway/react-buttons';
import {Link} from '@homeaway/react-localization-components';
import {Waypoint} from '@homeaway/react-unveil';

import AutoCancellation from './AutoCancellation/AutoCancellation';
import Cancellation from './Cancellation/Cancellation';
import {CancellationRequest} from './CancelTripModalMutations';

import StayContext from '../../../../context/StayContext';
import Spinner from '../../../../atoms/Spinner/Spinner';
import translate from '../../../../../utils/translations';
import {messageOwner} from '../../../../../utils/messageOwner';
import {LOCALES} from '../../../../constants';
import {INTENTS, SKILL_TYPE} from '../../../../molecules/VaFloating/constants';
import {TRIP_DETAILS} from '../../../../../utils/edapUtils';
import {VaControllerContext} from '../../../../context/VaControllerContext';

import {
    bookingRequestCancellationDismissed,
    bookingRequestCancellationSubmitted,
    bookingRequestCancellationSucceeded,
    cancelBookingRequestPresented,
    cancelBookingRequestSelected,
    MODIFY_REQUEST,
} from '../../../../../utils/EDAP/ConversationDetails';
import {getReservationUuidFromStayContextData} from '../../../../../utils/stayDomain.js';

import './CancelTripModal.less';

const CANCEL_TYPES = {
    AUTO_CANCELLATION: 'autocancel',
    CANCELLATION_REQUEST: 'canceltrip',
    WITHDRAWAL: 'withdrawtrip',
};

export class CancelTripModal extends Component {
    static propTypes = {
        actionLocation: PropTypes.string,
        cancellationReasonsByCategory: PropTypes.arrayOf(
            PropTypes.shape({
                category: PropTypes.string,
                descriptionText: PropTypes.string,
                reasons: PropTypes.arrayOf(
                    PropTypes.shape({
                        reason: PropTypes.string,
                        text: PropTypes.string,
                    })
                ),
                text: PropTypes.string,
            })
        ),
        cancellationType: PropTypes.string,
        conversationId: PropTypes.string,
        fullLocale: PropTypes.string,
        locale: PropTypes.string,
        onComplete: PropTypes.func,
        priceDetails: PropTypes.shape({}),
        shortDisplayName: PropTypes.string,
        translations: PropTypes.shape({}),
    };

    static defaultProps = {
        disableSubmit: false,
        fullLocale: 'en_US_HA',
        onComplete: () => {},
        translations: {},
    };

    constructor(props) {
        super(props);

        this.state = this.initialState;
    }

    componentDidMount() {
        const {cancellationType} = this.props;

        if (cancellationType === 'AUTO_CANCELLATION') {
            edap().push({
                name: 'generic.event',
                data: {
                    eventcategory: 'traveler activity',
                    eventaction: 'manage your booking view',
                    eventvalue: 'cancel booking',
                },
            });
        }
        this.reservationUUID = getReservationUuidFromStayContextData(
            this.stayContext
        );
    }

    static contextType = StayContext;

    initialState = {
        cancellationRequest: {
            refundEmailMessage: '',
            refundMessage: '',
            refundTitle: '',
            responseText: '',
            responseTitle: '',
        },
        category: {},
        hasError: false,
        isCancellationSucceded: false,
        isLoading: false,
        isOpen: false,
        message: '',
        reason: {},
        step: 0,
        travelerModificationError: null,
        travelerModificationSubmitted: false,
        type: CANCEL_TYPES[this.props.cancellationType],
    };

    renderCancelCta = () => (
        <Button
            className={'hidden-print cancel-trip-modal-btn'}
            label={this.props.manageBookingLabel}
            onClick={this.handleOpen}
        />
    );

    showTravelerModificationFlow = (reason) => {
        return (
            reason &&
            includes(
                [
                    'CHANGED_DATES',
                    'CHANGED_GUESTS',
                    'DONT_WANT_TRAVEL',
                    'OWNER_REQUEST',
                    'NONE_OF_ABOVE',
                ],
                reason.reason
            )
        );
    };

    get stayContext() {
        if (!this.context) {
            throw new Error(
                'stayContext must be used within a StayContextProvider'
            );
        }
        return this.context;
    }

    handleOpen = () => {
        this.setState({isOpen: true});
        this.cancelBookingSelectedEvent();
        edap().push({
            name: 'generic.modal',
            data: {
                modalaction: 'open',
                modalname: this.state.type,
            },
        });
    };

    updateVaContext = (vaContext) => {
        this.cancelBookingSelectedEvent();
        const eventsInfo = {
            actionLocationEDAP: TRIP_DETAILS,
            skillType: SKILL_TYPE.VRBO_CANCEL,
        };
        vaContext.actions.updateVaController(
            true,
            eventsInfo,
            INTENTS.VRBO_CANCEL
        );
        vaContext.actions.updateIsLoading(true);
    };

    cancelBookingSelectedEvent = () => {
        const {actionLocation} = this.props;
        const modalTypeIsNotWithdrawl =
            CANCEL_TYPES.WITHDRAWAL !== this.state.type;
        if (modalTypeIsNotWithdrawl) {
            cancelBookingRequestSelected({
                actionLocation,
                reservationUUID: this.reservationUUID,
            });
        }
    };

    handleClose = () => {
        const {
            cancellationRequest,
            category: {category},
            step,
            type,
        } = this.state;
        const {actionLocation} = this.props;
        const showsBookWithConfidenceMessage = category === 'OTHER';

        if (cancellationRequest.responseCode === 'REFUND_FAILED') {
            this.setState({...this.initialState});
        }
        if (this.userDismissedThePopup()) {
            bookingRequestCancellationDismissed({
                actionLocation,
                reservationUUID: this.reservationUUID,
            });
        }
        // If we are on the submit cancel step and we close the modal fire this special edap for auto cancellation
        if (
            type === CANCEL_TYPES.AUTO_CANCELLATION &&
            ((step === 3 && !showsBookWithConfidenceMessage) ||
                (step === 4 && showsBookWithConfidenceMessage))
        ) {
            edap().push({
                name: 'generic.click',
                data: {
                    eventcategory: 'traveler activity',
                    eventaction: 'message tab',
                    eventlabel: 'dont.cancel',
                },
            });
        } else {
            edap().push({
                name: 'generic.modal',
                data: {
                    modalaction: 'close',
                    modalname: type,
                },
            });
        }

        this.handleDone();
    };

    userDismissedThePopup = () => {
        const {isCancellationSucceded, type} = this.state;
        if (!isCancellationSucceded && CANCEL_TYPES.WITHDRAWAL !== type) {
            return true;
        }
        return false;
    };

    decreaseStep = (count = 1) => {
        const {type} = this.state;

        if (this.state.step - count >= 0) {
            this.setState({step: this.state.step - count});

            edap().push({
                name: 'generic.click',
                data: {
                    eventaction: `${
                        CANCEL_TYPES.AUTO_CANCELLATION === type ? 'auto' : ''
                    }cancellation.back`,
                    eventcategory: 'traveler activity',
                },
            });
        }
    };

    increaseStep = () => {
        this.setState({step: this.state.step + 1});
    };

    onSelectCategory = (category) => {
        const {type} = this.state;
        this.setState({category});

        edap().push({
            name: 'generic.click',
            data: {
                eventaction: `${
                    CANCEL_TYPES.AUTO_CANCELLATION === type ? 'auto' : ''
                }cancellation.category`,
                eventcategory: 'traveler activity',
                eventlabel: category.category,
            },
        });

        this.increaseStep();
    };

    onSelectReason = (reason) => {
        const {
            category: {category},
            type,
        } = this.state;

        this.setState({reason});

        edap().push({
            name: 'generic.click',
            data: {
                eventaction: `${
                    CANCEL_TYPES.AUTO_CANCELLATION === type ? 'auto' : ''
                }cancellation.reason`,
                eventcategory: 'traveler activity',
                eventlabel: `${category}:${reason.reason}`,
            },
        });

        this.checkDisableSubmit();
        this.increaseStep();
    };

    onFormChange = (event) => {
        this.setState({message: event.target.value});
        this.checkDisableSubmit();
    };

    checkDisableSubmit = () => {
        this.setState((prevState) => ({
            disableSubmit:
                (!prevState.message &&
                    prevState.reason.reason === 'NONE_OF_ABOVE') ||
                prevState.message.length > 1000,
        }));
    };

    onSubmit = (autoCancellationRequest = false) => {
        const {
            reason: {reason},
            category: {category},
            type,
        } = this.state;
        const {translations, actionLocation} = this.props;
        const {arrival, departure, guests} = this.stayContext.stay.booking;
        const {numberOfAdults: adults, numberOfChildren: children} =
            guests || {};

        if (CANCEL_TYPES.WITHDRAWAL !== type) {
            bookingRequestCancellationSubmitted({
                actionLocation,
                adults,
                children,
                category,
                reason,
                reservationUUID: this.reservationUUID,
                reservationArrival: arrival,
                reservationDeparture: departure,
            });
        }
        this.setState({isLoading: true});

        this.props
            .cancellationRequest({
                variables: {
                    request: {
                        conversationUuid: this.props.conversationId,
                        expectedRefundAmountDecimal: autoCancellationRequest
                            ? this.props.refundableTotalDecimalAmount
                            : null,
                        message: this.state.message,
                        reasonCode: this.state.reason.reason,
                    },
                },
            })
            .then(({data}) => {
                this.handleBookingCancellationEvents(
                    data.cancellationRequest.responseCode
                );
                this.setState({...data});
            })
            .catch(() => {
                this.setState({
                    hasError: true,
                    cancellationRequest: {
                        responseText: translate(
                            translations,
                            'trav-dash/trav-home',
                            'messages',
                            'th.inbox.conversation.cancel.modal.error'
                        ),
                        responseTitle: translate(
                            translations,
                            'trav-dash/trav-home',
                            'messages',
                            'th.inbox.error.title'
                        ),
                    },
                });
            })
            .then(() => {
                this.onRequestSentDone();
            });
    };

    handleBookingCancellationEvents = (responseCode) => {
        const {
            reason: {reason},
            type,
            message,
        } = this.state;
        const {conversationId, actionLocation} = this.props;
        const cancellationreason = `${message};${reason};${conversationId}`;

        if (responseCode === 'REFUND_FAILED') {
            edap().push({
                name: 'confirmed.reservation.cancellation.failure',
                data: {
                    conversationId,
                    cancellationreason,
                },
            });
            // If auto cancellation fails with REFUND_FAILED then we attempt to retry with request cancellation
            this.setState({type: CANCEL_TYPES.CANCELLATION_REQUEST});
        } else if (type !== CANCEL_TYPES.WITHDRAWAL) {
            bookingRequestCancellationSucceeded({
                actionLocation,
                reservationUUID: this.reservationUUID,
            });
            this.setState({isCancellationSucceded: true});
        }
    };

    onRequestSentDone = () => {
        const {type} = this.state;
        const reservationid = this.reservationUUID;
        const cancellationreason = `${this.state.message};${this.state.reason.reason};${this.props.conversationId}`;

        if (CANCEL_TYPES.AUTO_CANCELLATION === type) {
            edap().push({
                name: 'confirmed.reservation.cancellation.submit',
                data: {
                    reservationid,
                    cancellationreason,
                },
            });
        } else if (CANCEL_TYPES.WITHDRAWAL === type) {
            edap().push({
                name: 'pending.reservation.cancellation.submit',
                data: {
                    reservationid,
                    cancellationreason,
                },
            });
        } else if (CANCEL_TYPES.CANCELLATION_REQUEST === type) {
            this.setState({isCancellationSucceded: true});
            edap().push({
                name: 'reservation.cancellation.request.submit',
                data: {
                    reservationid,
                    cancellationreason,
                },
            });
        }

        this.increaseStep();
        this.setState({
            isLoading: false,
        });
    };

    bwcClose = () => {
        edap().push({
            name: 'generic.click',
            data: {
                eventaction: 'bwc.cancellation.close',
                eventcategory: 'traveler activity',
                eventlabel: this.state.reason.reason,
            },
        });

        // initiate cancellation from the start if user clicks 'I don't want to cancel' button on any of the reasons in 'OTHER' category
        // Using timeout to wait for animation cycle to finish before resetting state.
        setTimeout(() => {
            this.setState({...this.initialState});
        });

        this.handleClose();
    };

    bwcContinue = () => {
        edap().push({
            name: 'generic.click',
            data: {
                eventaction: 'bwc.cancellation.continue',
                eventcategory: 'traveler activity',
                eventlabel: this.state.reason.reason,
            },
        });

        this.increaseStep();
    };

    handleContinueCancellation = () => {
        edap().push({
            name: 'generic.click',
            data: {
                eventcategory: 'traveler activity',
                eventaction: 'refund.view',
                eventvalue: 'continue.with.cancellation',
            },
        });
        this.increaseStep();
    };

    handleDone = () => {
        this.setState({isOpen: false});
        this.props.onComplete();

        if (
            this.state.travelerModificationSubmitted ||
            this.state.travelerModificationError
        ) {
            // Using timeout to wait for animation cycle to finish before resetting state.
            setTimeout(() => {
                this.setState({...this.initialState});
            });
        }
    };

    handleSimilarPropertiesClick = () => {
        edap().push({
            name: 'generic.click',
            data: {
                eventcategory: 'traveler activity',
                eventaction: 'similar properties nearby',
            },
        });
    };

    submitTravelerModification = () => {
        const {fullLocale, conversationId, translations} = this.props;
        const reservationid = this.reservationUUID;
        const {message, reason} = this.state;

        let data = new FormData();
        const bookingChangeRequest = translate(
            translations,
            'trav-dash/trav-home',
            'messages',
            'bookingChange.conversation.header'
        );
        const reasonLabel = translate(
            translations,
            'trav-dash/trav-home',
            'messages',
            'bookingChange.reason.label'
        );

        data.append(
            'messageText',
            `${bookingChangeRequest}\n${reasonLabel} ${reason.text}\n\n${message}`
        );
        data.append('conversationId', conversationId);

        this.setState(() => ({
            isLoading: true,
        }));

        messageOwner(data, fullLocale).then((response) => {
            const hasError =
                isNil(response.body) || !isNil(response.body.violations);
            edap().push({
                name: `reservation.change.submit:${reason.reason}`,
                data: {
                    reservationid,
                    conversationid: conversationId,
                },
            });

            this.setState(() => ({
                travelerModificationError: hasError,
                travelerModificationSubmitted: !hasError,
                isLoading: false,
            }));
        });
    };

    renderAutoCancellationButtons = () => {
        const {
            autoCancellationSummary,
            location,
            recommendedListings,
            translations,
        } = this.props;
        const {
            cancellationRequest,
            hasError,
            reason,
            travelerModificationSubmitted,
        } = this.state;
        const showsBookWithConfidenceMessage =
            this.state.category.category === 'OTHER';
        const showsTravelerModButtons =
            this.showTravelerModificationFlow(reason);

        let footer = [];

        if (this.state.step === 0) {
            footer.push(
                <div
                    className="CancelTrip__buttons"
                    key={'auto-cancel-continue-button'}
                >
                    <ButtonPrimary
                        className="pull-right"
                        label={autoCancellationSummary.continueLabel}
                        onClick={this.handleContinueCancellation}
                        size={'sm'}
                        type={'submit'}
                    />
                </div>
            );
        }

        if (this.state.step === 1 || this.state.step === 2) {
            footer.push(
                <Button
                    key={'back-button'}
                    label={translate(
                        translations,
                        'trav-dash/trav-home',
                        'messages',
                        'th.button.back'
                    )}
                    onClick={() => {
                        // eslint-disable-line react/jsx-no-bind
                        this.decreaseStep();
                    }}
                    size={'sm'}
                />
            );
        }

        if (this.state.step === 3 && showsBookWithConfidenceMessage) {
            footer.push(
                <Button
                    key={'continue-button'}
                    label={translate(
                        translations,
                        'trav-dash/trav-home',
                        'messages',
                        'th.inbox.conversation.cancel.bwc.continue'
                    )}
                    onClick={this.bwcContinue}
                    size={'sm'}
                />
            );
            footer.push(
                <ButtonPrimary
                    className={'pull-right'}
                    key={'close-button'}
                    label={translate(
                        translations,
                        'trav-dash/trav-home',
                        'messages',
                        'th.inbox.conversation.cancel.bwc.back'
                    )}
                    onClick={this.bwcClose}
                    size={'sm'}
                />
            );
        } else if (
            (this.state.step === 3 && !showsBookWithConfidenceMessage) ||
            (this.state.step === 4 && showsBookWithConfidenceMessage)
        ) {
            // Traveler modification flow lets you go back, cancel the booking, OR request the booking change.
            if (showsTravelerModButtons && !travelerModificationSubmitted) {
                footer.push(
                    <Button
                        key={'go-back'}
                        label={translate(
                            translations,
                            'trav-dash/trav-home',
                            'messages',
                            'th.button.back'
                        )}
                        onClick={() => this.decreaseStep()} // eslint-disable-line react/jsx-no-bind
                        size={'sm'}
                    />
                );
                footer.push(
                    <Button
                        key={'cancel'}
                        label={this.props.manageBookingLabel}
                        disabled={this.state.disableSubmit}
                        onClick={() => this.onSubmit(true)} // eslint-disable-line react/jsx-no-bind
                        size={'sm'}
                    />
                );
                footer.push(
                    <ButtonPrimary
                        className={'pull-right'}
                        disabled={this.state.disableSubmit}
                        key={'submit-button'}
                        label={translate(
                            translations,
                            'trav-dash/trav-home',
                            'messages',
                            'th.inbox.conversation.cancel.change.button'
                        )}
                        onClick={this.submitTravelerModification} // eslint-disable-line react/jsx-no-bind
                        size={'sm'}
                        type={'submit'}
                    />
                );
            } else if (!travelerModificationSubmitted) {
                footer.push(
                    <Button
                        key={'back-button'}
                        label={translate(
                            translations,
                            'trav-dash/trav-home',
                            'messages',
                            'th.button.back'
                        )}
                        onClick={() => {
                            // eslint-disable-line react/jsx-no-bind
                            this.decreaseStep();
                        }}
                        size={'sm'}
                    />
                );
                footer.push(
                    <ButtonPrimary
                        className={'pull-right'}
                        disabled={this.state.disableSubmit}
                        key={'submit-button'}
                        label={this.props.manageBookingLabel}
                        onClick={() => this.onSubmit(true)} // eslint-disable-line react/jsx-no-bind
                        size={'sm'}
                        type={'submit'}
                    />
                );
            }
        }

        if (this.state.step === 3 && travelerModificationSubmitted) {
            // Hide cancellation-related buttons on traveler modification confirmation screen.
            return null;
        }

        if (
            cancellationRequest &&
            cancellationRequest.responseCode === 'REFUND_FAILED'
        ) {
            footer.push(
                <div
                    className="CancelTrip__buttons"
                    key={'retry-cancel-booking'}
                >
                    <ButtonPrimary
                        className={'pull-right'}
                        label={translate(
                            translations,
                            'trav-dash/trav-home',
                            'messages',
                            'th.inbox.conversation.cancel.cta'
                        )}
                        onClick={() => this.onSubmit(false)} // eslint-disable-line react/jsx-no-bind
                        size={'sm'}
                        type={'submit'}
                    />
                </div>
            );
        }

        if (
            (this.state.step === 4 &&
                !showsBookWithConfidenceMessage &&
                cancellationRequest.responseCode !== 'REFUND_FAILED') ||
            (this.state.step === 5 &&
                cancellationRequest.responseCode !== 'REFUND_FAILED') ||
            this.state.step === 6
        ) {
            footer.push(
                <Fragment>
                    {!hasError &&
                        cancellationRequest.responseCode !==
                            'CANCELLATION_PENDING_APPROVAL' && (
                            <div
                                className="CancelTrip__buttons"
                                key={'similar-properties-container-button'}
                            >
                                {recommendedListings &&
                                recommendedListings.length > 0 ? (
                                    <ButtonPrimary
                                        key={'done-button'}
                                        className="pull-right"
                                        label={translate(
                                            translations,
                                            'trav-dash/trav-home',
                                            'messages',
                                            'th.button.done'
                                        )}
                                        onClick={this.handleDone}
                                    />
                                ) : (
                                    <Link href={`/results?q=${location}`}>
                                        {(localizedUrl) => (
                                            <ButtonPrimary
                                                href={localizedUrl}
                                                key={
                                                    'search-for-other-properties'
                                                }
                                                label={translate(
                                                    translations,
                                                    'trav-dash/trav-home',
                                                    'messages',
                                                    'th.inbox.conversation.cancel.modal.button.similarProperties'
                                                )}
                                                onClick={
                                                    this
                                                        .handleSimilarPropertiesClick
                                                }
                                                target={'_blank'}
                                            />
                                        )}
                                    </Link>
                                )}
                            </div>
                        )}
                </Fragment>
            );
        }

        return footer;
    };

    renderCancellationButtons = () => {
        const {translations} = this.props;
        const {category, step} = this.state;
        const showsBookWithConfidenceMessage = category.category === 'OTHER';
        let footer = [];

        if (
            step === 1 ||
            (step === 2 && !showsBookWithConfidenceMessage) ||
            (step === 3 && showsBookWithConfidenceMessage)
        ) {
            footer.push(
                <Button
                    key={'back-button'}
                    label={translate(
                        translations,
                        'trav-dash/trav-home',
                        'messages',
                        'th.button.back'
                    )}
                    onClick={() => {
                        // eslint-disable-line react/jsx-no-bind
                        this.decreaseStep();
                    }}
                    size={'sm'}
                />
            );

            if (
                (step === 2 && !showsBookWithConfidenceMessage) ||
                (step === 3 && showsBookWithConfidenceMessage)
            ) {
                footer.push(
                    <ButtonPrimary
                        className={'pull-right'}
                        disabled={this.state.disableSubmit}
                        key={'submit-button'}
                        label={this.props.manageBookingLabel}
                        onClick={() => this.onSubmit(false)} // eslint-disable-line react/jsx-no-bind
                        size={'sm'}
                        type={'submit'}
                    />
                );
            }
        }

        if (step === 2 && showsBookWithConfidenceMessage) {
            footer.push(
                <Button
                    key={'continue-button'}
                    label={translate(
                        translations,
                        'trav-dash/trav-home',
                        'messages',
                        'th.inbox.conversation.cancel.bwc.continue'
                    )}
                    onClick={this.bwcContinue}
                    size={'sm'}
                />
            );
            footer.push(
                <ButtonPrimary
                    className={'pull-right'}
                    key={'close-button'}
                    label={translate(
                        translations,
                        'trav-dash/trav-home',
                        'messages',
                        'th.inbox.conversation.cancel.bwc.back'
                    )}
                    onClick={this.bwcClose}
                    size={'sm'}
                />
            );
        }

        return footer;
    };

    onSelfServeModalButtonEnter = () => {
        const {actionLocation} = this.props;
        const experienceType = MODIFY_REQUEST;
        cancelBookingRequestPresented({
            actionLocation,
            experienceType,
            reservationUUID: this.reservationUUID,
        });
    };

    render() {
        const {
            autoCancellationSummary,
            cancellationReasonsByCategory,
            location,
            locale,
            manageBookingLabel,
            priceDetails,
            recommendedListings,
            requestMarker,
            shortDisplayName,
            translations,
            type,
        } = this.props;
        const {
            cancellationRequest,
            category,
            hasError,
            message,
            reason,
            step,
            travelerModificationSubmitted,
            travelerModificationError,
        } = this.state;
        const showsBookWithConfidenceMessage = category.category === 'OTHER';
        let footer = null;
        let cancelType = null;
        if (this.props.cancellationType === 'AUTO_CANCELLATION') {
            cancelType = (
                <AutoCancellation
                    autoCancellationSummary={autoCancellationSummary}
                    cancellationReasonsByCategory={
                        cancellationReasonsByCategory
                    }
                    category={category}
                    hasError={hasError}
                    errorMessage={cancellationRequest.responseText}
                    errorTitle={cancellationRequest.responseTitle}
                    location={location}
                    manageBookingLabel={manageBookingLabel}
                    message={message}
                    onDone={this.handleDone}
                    onClose={this.handleClose}
                    onFormChange={this.onFormChange}
                    onSelectCategory={this.onSelectCategory}
                    onSelectReason={this.onSelectReason}
                    priceDetails={priceDetails}
                    reason={reason}
                    recommendedListings={recommendedListings}
                    refundEmailMessage={cancellationRequest.refundEmailMessage}
                    refundFailedSummary={
                        cancellationRequest.refundFailedSummary
                    }
                    refundMessage={cancellationRequest.refundMessage}
                    refundTitle={cancellationRequest.refundTitle}
                    responseCode={cancellationRequest.responseCode}
                    requestMarker={requestMarker}
                    showsBookWithConfidenceMessage={
                        showsBookWithConfidenceMessage
                    }
                    step={step}
                    translations={translations}
                    // Cancellations AB test (friction modal with traveler modifications)
                    goBack={this.decreaseStep}
                    shortDisplayName={shortDisplayName}
                    showsTravelerModificationFlow={this.showTravelerModificationFlow(
                        reason
                    )}
                    travelerModificationError={travelerModificationError}
                    travelerModificationSubmitted={
                        travelerModificationSubmitted
                    }
                />
            );
            footer = this.renderAutoCancellationButtons();
        } else {
            cancelType = (
                <Cancellation
                    cancellationReasonsByCategory={
                        cancellationReasonsByCategory
                    }
                    category={category}
                    hasError={hasError}
                    errorMessage={cancellationRequest.responseText}
                    errorTitle={cancellationRequest.responseTitle}
                    manageBookingLabel={manageBookingLabel}
                    message={message}
                    onDone={this.handleDone}
                    onFormChange={this.onFormChange}
                    onSelectCategory={this.onSelectCategory}
                    onSelectReason={this.onSelectReason}
                    reason={reason}
                    showsBookWithConfidenceMessage={
                        showsBookWithConfidenceMessage
                    }
                    step={step}
                    translations={translations}
                    type={type}
                />
            );
            footer = this.renderCancellationButtons();
        }

        return (
            <div className="CancelTrip">
                <Waypoint onEnter={this.onSelfServeModalButtonEnter}>
                    {locale === LOCALES.EN_US ? (
                        <Experiment name={'Vrbo_Stx_TDP_Cancel_Web'}>
                            <On>
                                <VaControllerContext.Consumer>
                                    {(vaContext) => (
                                        <div>
                                            <Button
                                                className={
                                                    'hidden-print cancel-trip-modal-btn'
                                                }
                                                disabled={
                                                    vaContext.store.isLoading
                                                }
                                                label={manageBookingLabel}
                                                onClick={() =>
                                                    this.updateVaContext(
                                                        vaContext
                                                    )
                                                }
                                            />
                                            <Spinner
                                                isLoading={
                                                    vaContext.store.isLoading
                                                }
                                            />
                                        </div>
                                    )}
                                </VaControllerContext.Consumer>
                            </On>
                            <Off>{this.renderCancelCta()}</Off>
                        </Experiment>
                    ) : (
                        <div>{this.renderCancelCta()}</div>
                    )}
                </Waypoint>
                <Modal
                    closeOnEsc={false}
                    closeOnOutsideClick={false}
                    footer={footer}
                    id={'cancel-trip-modal'}
                    isLoading={this.state.isLoading}
                    isOpen={this.state.isOpen}
                    onClose={this.handleClose}
                >
                    {cancelType}
                </Modal>
            </div>
        );
    }
}

export default compose(
    graphql(CancellationRequest, {name: 'cancellationRequest'})
)(CancelTripModal);
